import { useState, useEffect, useMemo } from "react";
import { Download, Edit2, Save, X, ArrowUpDown, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const ExcelTable = ({ 
  table, 
  tableIndex, 
  onExportCSV, 
  onSaveTable,
  onRenameTable,
  canRename = false,
  isEditable = true 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ colIndex: null, direction: 'desc' });
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [rowFilter, setRowFilter] = useState("");
  
  // State for the new checkbox columns (persisted with table data)
  const [checkboxState, setCheckboxState] = useState({});

  // Find the index of "Cut Length" (BMS) or "LEN" (Hilti) column — both serve
  // as the indicator for whether a row is something to cut. Rows with a value
  // here get the Cut ✓ checkbox; rows without get the Missing checkbox.
  const cutLengthColIndex = useMemo(() => {
    return table.headers.findIndex(h => {
      const hl = (h || '').toLowerCase().trim();
      return hl.includes('cut length') || hl === 'cutlength' || hl === 'len';
    });
  }, [table.headers]);

  // Initialize checkbox state from table data (if previously saved)
  useEffect(() => {
    const initialState = {};
    table.rows.forEach((row, rowIdx) => {
      // Check if cut_complete or missing_part data exists in extended row data
      const rowKey = `row_${rowIdx}`;
      initialState[rowKey] = {
        cutComplete: table.checkboxData?.[rowKey]?.cutComplete || false,
        missingPart: table.checkboxData?.[rowKey]?.missingPart || false
      };
    });
    setCheckboxState(initialState);
  }, [table.rows, table.checkboxData]);

  // Default-sort by LEN / Cut Length DESC whenever a new table is loaded.
  useEffect(() => {
    if (cutLengthColIndex !== -1) {
      setSortConfig({ colIndex: cutLengthColIndex, direction: 'desc' });
    } else {
      setSortConfig({ colIndex: null, direction: 'desc' });
    }
  }, [table.id, cutLengthColIndex]);

  // Check if a cut length value is non-zero
  const hasCutLength = (row) => {
    if (cutLengthColIndex === -1) return false;
    const value = row[cutLengthColIndex];
    if (!value || value.trim() === '') return false;
    const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return !isNaN(numValue) && numValue !== 0;
  };

  // Handle checkbox change - with auto-save
  const handleCheckboxChange = (rowIdx, field, checked) => {
    // Don't allow changes if not editable
    if (!isEditable) return;
    
    const rowKey = `row_${rowIdx}`;
    const newCheckboxState = {
      ...checkboxState,
      [rowKey]: {
        ...checkboxState[rowKey],
        [field]: checked
      }
    };
    setCheckboxState(newCheckboxState);
    
    // Auto-save immediately
    if (onSaveTable) {
      onSaveTable(table.id, { 
        headers: table.headers,
        rows: table.rows,
        checkboxData: newCheckboxState 
      });
    }
  };

  const handleStartEdit = () => {
    setEditedData({
      headers: [...table.headers],
      rows: table.rows.map(row => [...row])
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedData(null);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (onSaveTable && editedData) {
      // Include checkbox data in save
      onSaveTable(table.id, { 
        ...editedData, 
        checkboxData: checkboxState 
      });
    }
    setIsEditing(false);
    setEditedData(null);
  };

  const handleCellChange = (rowIdx, colIdx, value) => {
    setEditedData(prev => {
      const newRows = prev.rows.map((row, i) => 
        i === rowIdx ? row.map((cell, j) => j === colIdx ? value : cell) : [...row]
      );
      return { ...prev, rows: newRows };
    });
  };

  const handleHeaderChange = (colIdx, value) => {
    setEditedData(prev => ({
      ...prev,
      headers: prev.headers.map((h, i) => i === colIdx ? value : h)
    }));
  };

  // Remove a row from the editing buffer (admin-only via the Edit flow).
  const handleDeleteRow = (rowIdx) => {
    setEditedData(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== rowIdx),
    }));
    // Re-key checkbox state so rows below the deleted index shift up.
    setCheckboxState(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = parseInt(k.replace("row_", ""), 10);
        if (Number.isNaN(i) || i === rowIdx) return;
        const newIdx = i > rowIdx ? i - 1 : i;
        next[`row_${newIdx}`] = v;
      });
      return next;
    });
  };

  // Append a blank row.
  const handleAddRow = () => {
    setEditedData(prev => ({
      ...prev,
      rows: [...prev.rows, prev.headers.map(() => "")],
    }));
  };

  const handleSort = (colIndex) => {
    if (isEditing) return;
    setSortConfig(prev => ({
      colIndex,
      direction: prev.colIndex === colIndex && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedRows = () => {
    const rows = isEditing ? editedData.rows : table.rows;
    // Preserve original row index so checkbox state (keyed on the *original*
    // index) survives sorting and filtering.
    const indexed = rows.map((row, originalIdx) => ({ row, originalIdx }));

    // Apply free-text filter (case-insensitive, substring across all cells)
    const q = rowFilter.trim().toLowerCase();
    const filtered = q
      ? indexed.filter(({ row }) =>
          row.some(cell => String(cell || "").toLowerCase().includes(q))
        )
      : indexed;

    if (sortConfig.colIndex === null) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = a.row[sortConfig.colIndex] || '';
      const bVal = b.row[sortConfig.colIndex] || '';
      // Empty cells always sort to the bottom regardless of direction so
      // rows with real LEN values stay at the top.
      const aEmpty = aVal.toString().trim() === '';
      const bEmpty = bVal.toString().trim() === '';
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;
      const comparison = aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  };

  const headers = isEditing ? editedData.headers : table.headers;
  const rows = getSortedRows();
  
  // Extended headers with the two new columns (shorter names)
  const extendedHeaders = [...headers, 'Cut ✓', 'Missing'];

  // Generate column letters (A, B, C, ... Z, AA, AB, etc.)
  const getColumnLetter = (index) => {
    let letter = '';
    while (index >= 0) {
      letter = String.fromCharCode((index % 26) + 65) + letter;
      index = Math.floor(index / 26) - 1;
    }
    return letter;
  };

  // Append engineering drawing # for Hilti tables when available, so the
  // panel header shows e.g. "Hilti BOM — PF-0026 / 15-S-0014". Once an
  // admin has manually renamed the table, treat their text as authoritative
  // (no auto-appending) so they can override the suffix too. Also skip the
  // append if the name already contains a 15-S-XXXX-style suffix (e.g.
  // legacy renames that pre-date the `name_overridden` flag).
  const tableName = (() => {
    const base = table.table_name || `Table ${tableIndex + 1}`;
    if (table.name_overridden) return base;
    if (/\b15-S-\d{4,}\b/i.test(base)) return base;
    const eng = (table.metadata && table.metadata.eng_drawing_number) || '';
    if (eng && !base.includes(eng)) return `${base} / ${eng}`;
    return base;
  })();

  return (
    <div 
      className="bg-white border border-brand-200 rounded-xl overflow-hidden shadow-soft"
      data-testid={`excel-table-${tableIndex}`}
    >
      {/* Table Header Bar */}
      <div className="bg-brand-50 border-b border-brand-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isRenamingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-lg">
              <Input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!renameBusy && titleDraft.trim() && onRenameTable) {
                      setRenameBusy(true);
                      Promise.resolve(onRenameTable(table.id, titleDraft.trim()))
                        .then(() => { setIsRenamingTitle(false); })
                        .finally(() => setRenameBusy(false));
                    }
                  }
                  if (e.key === "Escape") { e.preventDefault(); setIsRenamingTitle(false); }
                }}
                className="h-8 text-sm font-heading font-semibold"
                maxLength={160}
                data-testid={`table-name-input-${tableIndex}`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-green-700 hover:text-white hover:bg-green-600"
                disabled={renameBusy || !titleDraft.trim()}
                onClick={() => {
                  if (!onRenameTable) return;
                  setRenameBusy(true);
                  Promise.resolve(onRenameTable(table.id, titleDraft.trim()))
                    .then(() => { setIsRenamingTitle(false); })
                    .finally(() => setRenameBusy(false));
                }}
                data-testid={`save-table-name-${tableIndex}-btn`}
              >
                <Save className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-brand-600"
                onClick={() => setIsRenamingTitle(false)}
                disabled={renameBusy}
                data-testid={`cancel-table-name-${tableIndex}-btn`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <h3 className="font-heading font-semibold text-brand-900 truncate" data-testid={`table-name-${tableIndex}`}>
                {tableName}
              </h3>
              {canRename && (
                <button
                  type="button"
                  onClick={() => { setTitleDraft(tableName); setIsRenamingTitle(true); }}
                  className="text-brand-500 hover:text-blue-600 transition-colors"
                  title="Rename table"
                  data-testid={`rename-table-${tableIndex}-btn`}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              )}
              <Badge variant="secondary" className="text-xs">
                Page {table.page_number}
              </Badge>
              <span className="text-sm text-brand-500">
                {table.row_count} rows × {table.col_count + 2} columns
              </span>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleCancelEdit}
                className="text-brand-600 hover:text-brand-900"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid={`save-table-${tableIndex}-btn`}
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            </>
          ) : (
            <>
              {isEditable && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={handleStartEdit}
                  className="text-brand-600 hover:text-brand-900"
                  data-testid={`edit-table-${tableIndex}-btn`}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onExportCSV(table.id)}
                className="border-green-200 text-green-700 hover:bg-green-50"
                data-testid={`export-table-${tableIndex}-csv-btn`}
              >
                <Download className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* In-table filter */}
      {!isEditing && (
        <div className="bg-white border-b border-brand-200 px-4 py-2 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Input
              value={rowFilter}
              onChange={(e) => setRowFilter(e.target.value)}
              placeholder="Filter rows by ART, description, LEN…"
              className="h-7 text-xs pr-7"
              data-testid={`row-filter-${tableIndex}`}
            />
            {rowFilter && (
              <button
                type="button"
                onClick={() => setRowFilter("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-brand-400 hover:text-brand-700"
                title="Clear filter"
                data-testid={`row-filter-clear-${tableIndex}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {rowFilter.trim() && (
            <span className="text-[11px] text-brand-500 whitespace-nowrap" data-testid={`row-filter-count-${tableIndex}`}>
              {getSortedRows().length} of {(isEditing ? editedData.rows : table.rows).length} rows
            </span>
          )}
        </div>
      )}

      {/* Excel-style Grid */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse excel-table">
          {/* Column Letters Row */}
          <thead>
            <tr className="bg-brand-100">
              <th className="excel-corner-cell"></th>
              {extendedHeaders.map((_, colIdx) => (
                <th 
                  key={`col-${colIdx}`}
                  className="excel-col-header"
                  onClick={() => colIdx < headers.length && handleSort(colIdx)}
                >
                  <div className="flex items-center justify-center gap-1">
                    {getColumnLetter(colIdx)}
                    {sortConfig.colIndex === colIdx && colIdx < headers.length && (
                      <ArrowUpDown className="w-3 h-3 text-blue-600" />
                    )}
                  </div>
                </th>
              ))}
              {isEditing && (
                <th className="excel-col-header">
                  <div className="flex items-center justify-center text-red-500">×</div>
                </th>
              )}
            </tr>
            {/* Headers Row */}
            <tr className="bg-brand-50">
              <th className="excel-row-header">1</th>
              {extendedHeaders.map((header, colIdx) => {
                const isCheckboxCol = colIdx >= headers.length;
                return (
                  <th key={`header-${colIdx}`} className={isCheckboxCol ? 'excel-checkbox-header bg-blue-50' : 'excel-header-cell'}>
                    {isEditing && colIdx < headers.length ? (
                      <Input
                        value={header}
                        onChange={(e) => handleHeaderChange(colIdx, e.target.value)}
                        className="h-6 text-xs font-semibold border-blue-300 focus:border-blue-500"
                      />
                    ) : (
                      <span className={`font-semibold text-xs ${isCheckboxCol ? 'text-blue-700' : 'text-brand-900'}`}>
                        {header}
                      </span>
                    )}
                  </th>
                );
              })}
              {isEditing && (
                <th className="excel-checkbox-header bg-red-50">
                  <span className="font-semibold text-xs text-red-700">Delete</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((entry, displayIdx) => {
              const row = entry.row;
              const rowIdx = entry.originalIdx;
              const rowKey = `row_${rowIdx}`;
              const rowHasCutLength = hasCutLength(row);
              const rowCheckbox = checkboxState[rowKey] || { cutComplete: false, missingPart: false };
              
              // Determine which checkbox should be enabled
              // Cut Complete: enabled when cut length > 0 AND Missing Part is unchecked
              // Missing Part: enabled when cut length = 0 or null
              const showCutCompleteCheckbox = rowHasCutLength && !rowCheckbox.missingPart;
              const showMissingPartCheckbox = !rowHasCutLength;
              
              return (
                <tr key={`row-${rowIdx}`} className="hover:bg-blue-50/30 transition-colors">
                  <td className="excel-row-header">{displayIdx + 2}</td>
                  {row.map((cell, cellIdx) => (
                    <td key={`cell-${rowIdx}-${cellIdx}`} className="excel-cell">
                      {isEditing ? (
                        <Input
                          value={cell}
                          onChange={(e) => handleCellChange(rowIdx, cellIdx, e.target.value)}
                          className="h-7 text-xs border-transparent focus:border-blue-400 bg-transparent hover:bg-white"
                        />
                      ) : (
                        <span className="text-brand-700">{cell}</span>
                      )}
                    </td>
                  ))}
                  {/* Cut Complete Column */}
                  <td className="excel-checkbox-cell bg-green-50/50">
                    <div className="flex items-center justify-center">
                      {showCutCompleteCheckbox ? (
                        <Checkbox
                          checked={rowCheckbox.cutComplete}
                          onCheckedChange={(checked) => handleCheckboxChange(rowIdx, 'cutComplete', checked)}
                          disabled={!isEditable}
                          className={`data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 h-4 w-4 ${!isEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
                          data-testid={`cut-complete-${tableIndex}-${rowIdx}`}
                        />
                      ) : (
                        <span className="text-brand-300 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  {/* Missing Part Column */}
                  <td className="excel-checkbox-cell bg-red-50/50">
                    <div className="flex items-center justify-center">
                      {showMissingPartCheckbox ? (
                        <Checkbox
                          checked={rowCheckbox.missingPart}
                          onCheckedChange={(checked) => handleCheckboxChange(rowIdx, 'missingPart', checked)}
                          disabled={!isEditable}
                          className={`data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 h-4 w-4 ${!isEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
                          data-testid={`missing-part-${tableIndex}-${rowIdx}`}
                        />
                      ) : (
                        <span className="text-brand-300 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  {/* Row delete column — only visible in edit mode */}
                  {isEditing && (
                    <td className="excel-checkbox-cell bg-red-50/30">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(rowIdx)}
                          className="inline-flex items-center justify-center w-6 h-6 text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded transition-colors"
                          title="Delete this row"
                          data-testid={`delete-row-${tableIndex}-${rowIdx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      <div className="bg-brand-50 border-t border-brand-200 px-4 py-2 flex items-center justify-between gap-2 text-xs text-brand-500">
        <span>Table ID: {table.id.slice(0, 8)}... • Extracted from page {table.page_number}</span>
        {isEditing && (
          <button
            type="button"
            onClick={handleAddRow}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 transition-colors"
            data-testid={`add-row-${tableIndex}-btn`}
          >
            <Plus className="w-3 h-3" />
            Add row
          </button>
        )}
      </div>
    </div>
  );
};

export default ExcelTable;
